import { useUnit } from "@/components/unit-provider";
import { useLocation } from "@/components/location-provider";
import {
  Activity,
  MapPin,
  Wind,
  Droplets,
  Gauge,
  Eye,
  Thermometer,
  TrendingUp,
  TrendingDown,
  Minus,
  Volume2,
} from "lucide-react";
import { WeatherParticles } from "@/components/dashboard/weather-particles";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { comfortIndex } from "@/lib/insights/comfort-index";
import { scoreSunset } from "@/lib/insights/sunset-quality";
import { useEffect } from "react";
import { useVoiceBriefing } from "@/lib/use-voice-briefing";
import { toast } from "sonner";

interface CurrentWeather {
  dt: number;
  temp: number;
  feels_like: number;
  humidity: number;
  dew_point?: number;
  pressure?: number;
  visibility?: number;
  wind_speed?: number;
  wind_deg?: number;
  wind_gust?: number;
  uvi?: number;
  clouds?: number;
  sunrise?: number;
  sunset?: number;
  weather: { id: number; icon: string; description: string; main: string }[];
}

interface HourlyEntry {
  dt: number;
  temp: number;
  pop?: number;
  clouds?: number;
  humidity?: number;
  visibility?: number;
  pressure?: number;
  weather?: { id: number; icon: string; description: string }[];
}

interface DailyEntry {
  temp: { max: number; min: number };
  sunrise?: number;
  sunset?: number;
  clouds?: number;
  humidity?: number;
}

interface AtmosphericHeroProps {
  current: CurrentWeather;
  hourly?: HourlyEntry[];
  daily?: DailyEntry[];
  aqi?: { list?: { main?: { aqi?: number } }[] };
}

function getAtmosphere(
  weatherId: number,
  isDay: boolean,
): { gradient: string; accent: string; accentRgb: string } {
  if (weatherId >= 200 && weatherId < 300) {
    return {
      gradient: "from-purple-950 via-slate-900 to-slate-950",
      accent: "oklch(0.6 0.2 280)",
      accentRgb: "120 80 200",
    };
  }
  if (weatherId >= 300 && weatherId < 600) {
    return {
      gradient: "from-blue-950 via-slate-900 to-slate-950",
      accent: "oklch(0.55 0.18 240)",
      accentRgb: "50 100 220",
    };
  }
  if (weatherId >= 600 && weatherId < 700) {
    return {
      gradient: "from-slate-700 via-slate-800 to-slate-950",
      accent: "oklch(0.85 0.05 220)",
      accentRgb: "200 210 240",
    };
  }
  if (weatherId >= 700 && weatherId < 800) {
    return {
      gradient: "from-slate-800 via-slate-900 to-slate-950",
      accent: "oklch(0.6 0.05 240)",
      accentRgb: "140 150 170",
    };
  }
  if (weatherId === 800) {
    return isDay
      ? {
          gradient: "from-sky-900 via-indigo-950 to-slate-950",
          accent: "oklch(0.85 0.18 75)",
          accentRgb: "255 200 80",
        }
      : {
          gradient: "from-indigo-950 via-purple-950 to-slate-950",
          accent: "oklch(0.7 0.15 280)",
          accentRgb: "160 120 220",
        };
  }
  return {
    gradient: "from-slate-800 via-slate-900 to-slate-950",
    accent: "oklch(0.7 0.1 220)",
    accentRgb: "140 160 200",
  };
}

const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
function dirLabel(deg?: number) {
  if (deg == null || isNaN(deg)) return "";
  return COMPASS[Math.round(deg / 45) % 8];
}

function fmtTime(ts?: number) {
  if (!ts) return "";
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function fmtHour(ts: number) {
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric" });
}

export function AtmosphericHero({ current, hourly, daily, aqi }: AtmosphericHeroProps) {
  const { display } = useUnit();
  const { current: location } = useLocation();
  const w = current.weather?.[0];
  const isDay = w?.icon?.endsWith("d") ?? true;
  const atmosphere = getAtmosphere(w?.id || 800, isDay);
  const {
    speakBriefing: speakServerBriefing,
    speakText,
    stop,
    speaking,
    supported,
    error,
  } = useVoiceBriefing();

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const today = daily?.[0];
  const hi = today?.temp?.max;
  const lo = today?.temp?.min;
  const range = hi != null && lo != null && hi !== lo ? hi - lo : null;
  const rangePos =
    range && hi != null && lo != null
      ? Math.max(0, Math.min(1, (current.temp - lo) / range))
      : null;

  // Comfort + sunset chips
  const aqiVal = aqi?.list?.[0]?.main?.aqi;
  const comfort = comfortIndex({
    tempC: current.temp,
    feelsLikeC: current.feels_like,
    humidity: current.humidity,
    windMs: current.wind_speed ?? 0,
    uvi: current.uvi ?? 0,
    aqi: aqiVal,
  });

  // Use hourly entry near sunset for cloud/humidity at sunset.
  const sunsetTs = today?.sunset ?? current.sunset;
  let sunsetCloud = current.clouds ?? 50;
  let sunsetHumidity = current.humidity;
  let sunsetVis = current.visibility;
  if (sunsetTs && hourly?.length) {
    let nearest = hourly[0];
    let bestDelta = Math.abs(nearest.dt - sunsetTs);
    for (const h of hourly) {
      const d = Math.abs(h.dt - sunsetTs);
      if (d < bestDelta) {
        bestDelta = d;
        nearest = h;
      }
    }
    sunsetCloud = nearest.clouds ?? sunsetCloud;
    sunsetHumidity = nearest.humidity ?? sunsetHumidity;
    sunsetVis = nearest.visibility ?? sunsetVis;
  }
  const sunsetQ = sunsetTs
    ? scoreSunset({
        cloudPct: sunsetCloud,
        humidity: sunsetHumidity,
        visibilityMeters: sunsetVis,
      })
    : null;

  const speakBriefing = async () => {
    if (speaking) {
      stop();
      return;
    }
    // Build a local fallback briefing in case the server can't reach Camb.ai.
    const tF = Math.round(current.feels_like);
    const desc = w?.description || "no specific conditions";
    const hiTxt = hi != null ? Math.round(hi) : null;
    const loTxt = lo != null ? Math.round(lo) : null;
    const wind = current.wind_speed != null ? Math.round(current.wind_speed) : null;
    const popPct = hourly?.length
      ? Math.round(Math.max(...hourly.slice(0, 6).map((h) => h.pop || 0)) * 100)
      : 0;
    const parts: string[] = [];
    parts.push(
      `In ${location.name}, it's ${Math.round(current.temp)} degrees, ${desc}, feels like ${tF}.`,
    );
    if (hiTxt != null && loTxt != null) {
      parts.push(`Today's range: ${loTxt} to ${hiTxt}.`);
    }
    if (popPct >= 30) {
      parts.push(`${popPct} percent chance of precipitation in the next six hours.`);
    }
    if (wind != null && wind >= 8) {
      parts.push(`Wind ${wind} meters per second, ${dirLabel(current.wind_deg)}.`);
    }
    parts.push(`Comfort index ${comfort.score} out of 100, ${comfort.label}.`);
    if (sunsetQ && sunsetQ.score >= 7) {
      parts.push(`${sunsetQ.label} sunset expected.`);
    }
    const fallbackText = parts.join(" ");

    try {
      await speakServerBriefing({
        lat: location.lat,
        lon: location.lon,
        location: location.name,
      });
    } catch {
      // Server/Camb path failed entirely — speak with browser TTS so the
      // user still gets audio output.
      speakText(fallbackText);
    }
  };

  // Pressure trend: compare current to ~3h ago (hourly[3] is +3h, so trend looks at current vs near past).
  // We don't have past hourly here, so compare current to hourly[3] forecast as a "next 3h trend" hint.
  let pressureTrend: "up" | "down" | "flat" | null = null;
  let pressureDelta = 0;
  if (current.pressure != null && hourly && hourly.length > 3) {
    const future = (hourly[3] as { pressure?: number })?.pressure;
    if (typeof future === "number") {
      pressureDelta = future - current.pressure;
      pressureTrend =
        Math.abs(pressureDelta) < 0.5 ? "flat" : pressureDelta > 0 ? "up" : "down";
    }
  }

  const sunrise = today?.sunrise ?? current.sunrise;
  const sunset = today?.sunset ?? current.sunset;
  const sunProgress =
    sunrise && sunset && sunset > sunrise
      ? Math.max(0, Math.min(1, (current.dt - sunrise) / (sunset - sunrise)))
      : null;
  const isDaytime = sunrise && sunset ? current.dt >= sunrise && current.dt <= sunset : isDay;

  const next6 = (hourly || []).slice(1, 7);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${atmosphere.gradient} border border-border/20 shadow-2xl shadow-black/20`}
    >
      <WeatherParticles
        weatherId={w?.id || 800}
        isDay={isDay}
        className="absolute inset-0 z-0"
      />

      <div className="absolute inset-0 atmospheric-grid opacity-30 z-0" />
      <div
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-20 glow-pulse z-0"
        style={{ background: atmosphere.accent }}
      />
      <div
        className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full blur-3xl opacity-15 z-0"
        style={{ background: atmosphere.accent }}
      />

      <div className="relative p-6 lg:p-8 z-10 space-y-6">
        {/* TOP META ROW */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-white/85 uppercase tracking-[0.2em]">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-400 glow-pulse" />
            Live
          </span>
          <span className="text-white/30">·</span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3 w-3" />
            <span className="normal-case tracking-normal text-white">
              {location.name}
              {location.country ? `, ${location.country}` : ""}
            </span>
          </span>
          {current.uvi != null && (
            <>
              <span className="text-white/30">·</span>
              <Badge
                variant={
                  current.uvi > 7 ? "destructive" : current.uvi > 5 ? "warning" : "outline"
                }
                size="sm"
                className="!border-white/30 !text-white"
              >
                UV {current.uvi.toFixed(current.uvi < 1 ? 1 : 0)}
              </Badge>
            </>
          )}
          <span className="text-white/30">·</span>
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 normal-case tracking-normal text-[10px] font-semibold",
              comfort.color === "emerald" && "border-emerald-300/40 text-emerald-200 bg-emerald-400/10",
              comfort.color === "lime" && "border-lime-300/40 text-lime-200 bg-lime-400/10",
              comfort.color === "amber" && "border-amber-300/40 text-amber-200 bg-amber-400/10",
              comfort.color === "orange" && "border-orange-300/40 text-orange-200 bg-orange-400/10",
              comfort.color === "red" && "border-rose-300/40 text-rose-200 bg-rose-400/10",
            )}
            title={comfort.reasons.join(" · ")}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            Comfort {comfort.score}
            <span className="text-white/70 font-normal">· {comfort.label}</span>
          </span>
          {sunsetQ && (
            <>
              <span className="text-white/30">·</span>
              <span
                className="inline-flex items-center gap-1 rounded-full border border-white/30 bg-white/5 px-2 py-0.5 normal-case tracking-normal text-[10px] font-semibold text-white"
                title={sunsetQ.reason}
              >
                <span aria-hidden>{sunsetQ.emoji}</span>
                Sunset {sunsetQ.score}
                <span className="text-white/70 font-normal">· {sunsetQ.label}</span>
              </span>
            </>
          )}
          <span className="ml-auto inline-flex items-center gap-2 text-white/70 normal-case tracking-normal">
            {supported && (
              <button
                type="button"
                onClick={speakBriefing}
                aria-label={speaking ? "Stop briefing" : "Read voice briefing"}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border border-white/25 px-2.5 py-1 text-[11px] font-semibold transition-colors hover:bg-white/10",
                  speaking ? "bg-white/15 text-white" : "text-white/85",
                )}
                title="Voice briefing"
              >
                <Volume2 className={cn("h-3 w-3", speaking && "animate-pulse")} />
                <span className="hidden sm:inline">{speaking ? "Stop" : "Briefing"}</span>
              </button>
            )}
            <span className="hidden sm:inline">Updated</span>
            <span className="font-medium text-white/90">{fmtTime(current.dt)}</span>
          </span>
        </div>

        {/* MAIN ROW: temp + icon */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-3">
              <span className="text-7xl lg:text-[5.5rem] font-black tracking-tighter text-white drop-shadow-[0_0_40px_rgba(255,255,255,0.2)] leading-none">
                {display(current.temp)}
              </span>
            </div>
            <p className="mt-2 text-base capitalize text-white font-semibold tracking-tight">
              {w?.description}
            </p>
            <p className="text-sm text-white/85 font-medium">
              Feels like {display(current.feels_like)}
            </p>
          </div>

          <div className="flex-shrink-0">
            <div className="relative">
              <img
                src={`/api/icon/${w?.icon}@4x.png`}
                alt={w?.description}
                className="h-32 w-32 lg:h-40 lg:w-40 drop-shadow-[0_0_50px_rgba(255,255,255,0.3)] float-anim"
              />
              <div
                className="absolute inset-0 rounded-full blur-2xl opacity-30"
                style={{ background: atmosphere.accent }}
              />
            </div>
          </div>
        </div>

        {/* TODAY'S RANGE BAR */}
        {hi != null && lo != null && rangePos != null && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-[11px] text-white/75 uppercase tracking-wider font-medium">
              <span className="inline-flex items-center gap-1.5">
                <TrendingDown className="h-3 w-3 text-sky-300" />
                Low {display(lo)}
              </span>
              <span className="text-white/60 normal-case tracking-normal">
                Today&apos;s range
              </span>
              <span className="inline-flex items-center gap-1.5">
                High {display(hi)}
                <TrendingUp className="h-3 w-3 text-orange-300" />
              </span>
            </div>
            <div className="relative h-2 w-full rounded-full bg-white/10 overflow-hidden">
              <div className="absolute inset-y-0 left-0 right-0 bg-gradient-to-r from-sky-400/60 via-emerald-400/60 to-orange-400/70" />
              <div
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-4 rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.7)] ring-2 ring-white/40"
                style={{ left: `${rangePos * 100}%` }}
                aria-label={`Current ${display(current.temp)} between low and high`}
              />
            </div>
          </div>
        )}

        {/* METRIC PILLS */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {current.feels_like != null && (
            <MetricPill
              icon={<Thermometer className="h-3.5 w-3.5 text-orange-300" />}
              label="Feels"
              value={display(current.feels_like)}
            />
          )}
          {current.wind_speed != null && (
            <MetricPill
              icon={
                <span
                  className="inline-block"
                  style={{ transform: `rotate(${current.wind_deg ?? 0}deg)` }}
                >
                  <Wind className="h-3.5 w-3.5 text-teal-300" />
                </span>
              }
              label={`Wind ${dirLabel(current.wind_deg)}`}
              value={`${current.wind_speed.toFixed(1)} m/s`}
              sub={
                current.wind_gust && current.wind_gust > current.wind_speed
                  ? `gust ${current.wind_gust.toFixed(1)}`
                  : undefined
              }
            />
          )}
          {current.humidity != null && (
            <MetricPill
              icon={<Droplets className="h-3.5 w-3.5 text-blue-300" />}
              label="Humidity"
              value={`${current.humidity}%`}
            />
          )}
          {current.dew_point != null && (
            <MetricPill
              icon={<Droplets className="h-3.5 w-3.5 text-cyan-300" />}
              label="Dew point"
              value={display(current.dew_point)}
            />
          )}
          {current.pressure != null && (
            <MetricPill
              icon={<Gauge className="h-3.5 w-3.5 text-violet-300" />}
              label="Pressure"
              value={`${current.pressure}`}
              sub="hPa"
              trend={pressureTrend}
            />
          )}
          {current.visibility != null && (
            <MetricPill
              icon={<Eye className="h-3.5 w-3.5 text-slate-300" />}
              label="Visibility"
              value={`${(current.visibility / 1000).toFixed(1)}`}
              sub="km"
            />
          )}
        </div>

        {/* HOURLY STRIP */}
        {next6.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/65">
                Next 6 hours
              </h3>
              <span className="text-[10px] text-white/50">
                hourly · precip chance
              </span>
            </div>
            <div className="grid grid-cols-6 gap-1.5">
              {next6.map((h) => {
                const ico = h.weather?.[0]?.icon;
                const pop = Math.round((h.pop || 0) * 100);
                return (
                  <div
                    key={h.dt}
                    className="rounded-lg bg-white/5 border border-white/10 px-1 py-2 flex flex-col items-center gap-1"
                  >
                    <span className="text-[10px] font-medium text-white/70 uppercase tracking-wider">
                      {fmtHour(h.dt)}
                    </span>
                    {ico ? (
                      <img
                        src={`/api/icon/${ico}.png`}
                        alt=""
                        className="h-7 w-7 -my-1"
                      />
                    ) : (
                      <div className="h-7 w-7" />
                    )}
                    <span className="text-sm font-bold text-white tabular-nums">
                      {display(h.temp)}
                    </span>
                    <div
                      className="w-full h-0.5 rounded-full bg-white/10 overflow-hidden"
                      title={`${pop}% precipitation`}
                    >
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          pop > 50
                            ? "bg-blue-400"
                            : pop > 20
                              ? "bg-blue-400/60"
                              : "bg-white/20",
                        )}
                        style={{ width: `${pop}%` }}
                      />
                    </div>
                    <span className="text-[9px] tabular-nums text-white/55">{pop}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SUN TRACK + STATUS */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 pt-1">
          <GlassPanel
            intensity="high"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs !bg-white/10 !border-white/15 !shadow-none flex-shrink-0 self-start"
          >
            <Activity className="h-3 w-3 text-emerald-400" />
            <span className="text-white font-medium">System operational</span>
          </GlassPanel>

          {sunrise && sunset && (
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between text-[10px] text-white/70 uppercase tracking-wider mb-1.5">
                <span>☀ {fmtTime(sunrise)}</span>
                <span className="text-white/55 normal-case tracking-normal">
                  {isDaytime ? "Daylight" : "Night"}
                </span>
                <span>☾ {fmtTime(sunset)}</span>
              </div>
              <div className="relative h-1 rounded-full bg-white/10 overflow-hidden">
                <div className="absolute inset-y-0 left-0 right-0 bg-gradient-to-r from-amber-300/40 via-orange-300/60 to-indigo-400/40" />
                {sunProgress != null && isDaytime && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-amber-200 shadow-[0_0_10px_rgba(253,224,71,0.9)]"
                    style={{ left: `${sunProgress * 100}%` }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MetricPill({
  icon,
  label,
  value,
  sub,
  trend,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  trend?: "up" | "down" | "flat" | null;
}) {
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const trendColor =
    trend === "up"
      ? "text-emerald-300"
      : trend === "down"
        ? "text-rose-300"
        : "text-white/50";

  return (
    <div className="rounded-xl bg-white/[0.06] border border-white/10 px-3 py-2 flex items-center gap-2.5 backdrop-blur-sm">
      <span className="flex-shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-[9px] uppercase tracking-wider text-white/60 leading-tight font-semibold">
          {label}
        </p>
        <p className="text-sm font-bold text-white leading-tight tabular-nums truncate">
          {value}
          {sub && <span className="ml-1 text-[10px] font-normal text-white/65">{sub}</span>}
        </p>
      </div>
      {trend && (
        <TrendIcon className={cn("h-3.5 w-3.5 flex-shrink-0", trendColor)} aria-hidden />
      )}
    </div>
  );
}
